// One source table inside the import wizard: what it becomes (target), how
// its columns map onto the target's fields, per-table options and a preview
// of the rows (with the images embedded in the sheet) where rows can be
// left out.
import React, { useMemo, useState } from "react";
import { Alert, Checkbox, Collapse, Image, Input, InputNumber, Select, Space, Switch, Table, Tag, Tooltip, Typography } from "antd";
import { url } from "api";
import { activityLabel, activityTitle } from "helpers/schedule/activityLabel";
import { matchOption } from "components/SearchSelect/searchSelect";

const CONF_COLOR = { high: "green", medium: "gold", low: "default", template: "blue" };
const TARGET_COLOR = { schedule: "geekblue", issues: "volcano", checklist: "cyan", minutes: "purple", contacts: "magenta", skip: "default" };

const cellText = (v) => {
  if (v === undefined || v === null || v === "") return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "object" && v !== null && (v.$date || v instanceof Date)) return String(v);
  return String(v);
};
const dateish = (v) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v) ? v.slice(0, 10) : v);

export default function TableMappingCard({ table, tmap, targets, ganttItems = [], onChange }) {
  const [showAll, setShowAll] = useState(false);
  // Row search: filters the preview (any cell, section, row number);
  // selection is kept, and the header checkbox acts on the shown rows.
  const [rowSearch, setRowSearch] = useState("");
  const rowQuery = rowSearch.trim().toLowerCase();
  const target = tmap.target || "skip";
  const tdef = targets[target] || targets.skip;
  const fields = tmap.fields || {};
  const options = tmap.options || {};
  const enabled = tmap.enabled !== false && target !== "skip";

  const columnOptions = useMemo(() => [
    { value: "", label: "— not mapped —" },
    ...table.columns.map((c) => ({ value: c.key, label: `${c.header}${c.filled !== undefined ? ` (${c.filled})` : ""}` })),
  ], [table.columns]);

  const set = (patch) => onChange({ ...tmap, ...patch });
  const setField = (k, v) => set({ fields: { ...fields, [k]: v || undefined } });
  const setOption = (k, v) => set({ options: { ...options, [k]: v } });

  const selected = useMemo(() => {
    if (Array.isArray(tmap.selectedRows)) return new Set(tmap.selectedRows.map(Number));
    return new Set(table.rows.filter((r) => r.selected !== false).map((r) => Number(r.i)));
  }, [tmap.selectedRows, table.rows]);
  const setSelected = (next) => set({ selectedRows: [...next] });

  const matchingRows = useMemo(() => {
    if (!rowQuery) return table.rows;
    return table.rows.filter((r) => {
      const parts = [String((r.i || 0) + 1), r.section || ""];
      for (const c of table.columns) parts.push(cellText(dateish(r.cells ? r.cells[c.key] : "")));
      return parts.join(" ").toLowerCase().includes(rowQuery);
    });
  }, [table.rows, table.columns, rowQuery]);
  const previewRows = showAll || rowQuery ? matchingRows : matchingRows.slice(0, 25);
  const shownSelected = matchingRows.filter((r) => selected.has(Number(r.i))).length;
  const previewCols = [
    {
      title: <Checkbox
        indeterminate={shownSelected > 0 && shownSelected < matchingRows.length}
        checked={shownSelected === matchingRows.length && matchingRows.length > 0}
        title={rowQuery ? "Select / unselect the rows shown" : "Select / unselect all rows"}
        onChange={(e) => {
          const n = new Set(selected);
          for (const r of matchingRows) { if (e.target.checked) n.add(Number(r.i)); else n.delete(Number(r.i)); }
          setSelected(n);
        }}
      />,
      key: "sel", width: 36, fixed: "left",
      render: (_, r) => <Checkbox checked={selected.has(Number(r.i))} onChange={(e) => { const n = new Set(selected); if (e.target.checked) n.add(Number(r.i)); else n.delete(Number(r.i)); setSelected(n); }} />,
    },
    { title: "#", key: "i", width: 48, render: (_, r) => <Typography.Text type="secondary" style={{ fontSize: 11 }}>{r.i + 1}</Typography.Text> },
    ...(table.sections && table.sections.length ? [{ title: "Section", key: "section", width: 120, render: (_, r) => r.section ? <Tag>{r.section}</Tag> : null }] : []),
    ...table.columns.filter((c) => c.key !== "__options" || true).map((c) => {
      const mappedTo = Object.entries(fields).find(([, col]) => col === c.key);
      const fdef = mappedTo ? (tdef.fields || []).find((f) => f.key === mappedTo[0]) : null;
      return {
        title: (
          <div style={{ lineHeight: 1.2 }}>
            <div>{c.header}</div>
            {fdef && <Tag color="blue" style={{ fontSize: 10, marginTop: 2 }}>{fdef.label}</Tag>}
          </div>
        ),
        key: c.key, dataIndex: ["cells", c.key], width: 180, ellipsis: false,
        render: (v) => {
          const s = cellText(dateish(v));
          if (!s) return null;
          return <div style={{ maxHeight: 96, overflow: "auto", whiteSpace: "pre-wrap", fontSize: 12 }}>{s}</div>;
        },
      };
    }),
    ...(table.rows.some((r) => r.images && r.images.length) ? [{
      title: "Images", key: "images", width: 150,
      render: (_, r) => r.images && r.images.length ? (
        <Image.PreviewGroup>
          <Space size={2} wrap>
            {r.images.slice(0, 4).map((im) => <Image key={im.id} src={`${url}${im.url}`} width={40} height={30} style={{ objectFit: "cover" }} alt={im.name} />)}
            {r.images.length > 4 && <Tag>+{r.images.length - 4}</Tag>}
          </Space>
        </Image.PreviewGroup>
      ) : null,
    }] : []),
  ];

  const requiredMissing = (tdef.fields || []).filter((f) => f.required && !fields[f.key]);

  const header = (
    <Space wrap size={6} onClick={(e) => e.stopPropagation()}>
      <Switch size="small" checked={enabled} onChange={(v) => set({ enabled: v, target: v && target === "skip" ? (table.suggested?.target !== "skip" ? table.suggested.target : "schedule") : target })} />
      <b>{table.sheet ? table.sheet : ""}{table.sheet && table.name !== table.sheet ? " · " : ""}{table.name !== table.sheet ? table.name.replace(`${table.sheet} · `, "") : ""}</b>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{table.rowCount} row{table.rowCount === 1 ? "" : "s"}{table.hidden ? " · hidden sheet" : ""}</Typography.Text>
      <Select
        size="small" style={{ minWidth: 240 }} value={target}
        onChange={(v) => {
          // Switching target: re-suggest fields for that target from the table's stored scores when available.
          // Re-target: the parser's best mapping for that target (kept as
          // `alternatives`), or the original suggestion when it matches.
          const same = table.suggested && table.suggested.target === v;
          const suggestedFields = same ? table.suggested.fields : ((table.suggested && table.suggested.alternatives && table.suggested.alternatives[v]) || {});
          set({ target: v, enabled: v !== "skip", fields: { ...suggestedFields }, options: same ? (table.suggested.options || {}) : {} });
        }}
        options={Object.entries(targets).map(([k, t]) => ({ value: k, label: <span><Tag color={TARGET_COLOR[k]} style={{ marginRight: 6 }}>{k}</Tag>{t.label}</span> }))}
      />
      {table.suggested && (
        <Tooltip title={table.suggested.confidence === "template" ? `Mapped from saved template "${table.suggested.templateName || ""}"` : `Auto-detected (${table.suggested.confidence} confidence)`}>
          <Tag color={CONF_COLOR[table.suggested.confidence] || "default"}>{table.suggested.confidence === "template" ? "template" : `auto · ${table.suggested.confidence}`}</Tag>
        </Tooltip>
      )}
      {enabled && requiredMissing.length > 0 && <Tag color="red">map: {requiredMissing.map((f) => f.label).join(", ")}</Tag>}
      {enabled && <Tag>{selected.size} of {table.rowCount} rows</Tag>}
    </Space>
  );

  return (
    <Collapse size="small" style={{ marginBottom: 8, opacity: enabled ? 1 : 0.7 }} defaultActiveKey={enabled && (requiredMissing.length || (table.suggested && table.suggested.confidence !== "high" && table.suggested.confidence !== "template")) ? ["m"] : []}
      items={[{
        key: "m",
        label: header,
        children: !enabled ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>This table will not be imported. Turn it on to map it.</Typography.Text>
        ) : (
          <div>
            {table.title && <Typography.Text type="secondary" style={{ fontSize: 12 }}>Title in sheet: {table.title}</Typography.Text>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "4px 16px", marginTop: 6, marginBottom: 10 }}>
              {(tdef.fields || []).map((f) => (
                <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 150, fontSize: 12, color: f.required ? "#cf1322" : undefined }} title={f.key}>{f.label}{f.required ? " *" : ""}</span>
                  <Select filterOption={matchOption}
                    size="small" style={{ flex: 1 }} showSearch optionFilterProp="label"
                    value={fields[f.key] && !String(fields[f.key]).startsWith("=") ? fields[f.key] : ""}
                    options={columnOptions}
                    onChange={(v) => setField(f.key, v)}
                    dropdownRender={(menu) => (
                      <div>
                        {menu}
                        <div style={{ padding: "4px 8px", borderTop: "1px solid #eee" }}>
                          <Input size="small" placeholder="or type a fixed value for every row" defaultValue={String(fields[f.key] || "").startsWith("=") ? String(fields[f.key]).slice(1) : ""}
                            onPressEnter={(e) => setField(f.key, e.target.value ? `=${e.target.value}` : "")} onBlur={(e) => { if (e.target.value) setField(f.key, `=${e.target.value}`); }} />
                        </div>
                      </div>
                    )}
                  />
                  {String(fields[f.key] || "").startsWith("=") && <Tag color="purple" closable onClose={() => setField(f.key, "")}>= {String(fields[f.key]).slice(1)}</Tag>}
                </div>
              ))}
            </div>
            {(tdef.options || []).length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "4px 16px", marginBottom: 10, background: "#fafafa", padding: 8, borderRadius: 6 }}>
                {(tdef.options || []).map((o) => {
                  const val = options[o.key] !== undefined ? options[o.key] : o.default;
                  if (o.key === "parentName" && options.parentMode && !["table", "section"].includes(options.parentMode)) return null;
                  if (o.key === "parentId" && options.parentMode !== "existing") return null;
                  return (
                    <div key={o.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 210, fontSize: 12 }}>{o.label}</span>
                      {o.type === "boolean" && <Switch size="small" checked={!!val} onChange={(v) => setOption(o.key, v)} />}
                      {o.type === "select" && <Select size="small" style={{ flex: 1 }} value={val} options={o.choices.map((c) => ({ value: c, label: c }))} onChange={(v) => setOption(o.key, v)} />}
                      {o.type === "number" && <InputNumber size="small" min={0} value={val} onChange={(v) => setOption(o.key, v)} />}
                      {o.type === "text" && <Input size="small" style={{ flex: 1 }} value={val || ""} placeholder={o.key === "parentName" ? (table.sheet || table.name) : ""} onChange={(e) => setOption(o.key, e.target.value)} />}
                      {o.type === "ganttItem" && (
                        <Select filterOption={matchOption} size="small" style={{ flex: 1 }} allowClear showSearch optionFilterProp="label" value={val || undefined} placeholder="Schedule activity"
                          options={ganttItems.map((g) => ({ value: g._id, label: `${activityLabel(g)}${g.type === "meeting" ? " (meeting)" : ""}`, title: activityTitle(g) }))} onChange={(v) => setOption(o.key, v || "")} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {table.legend && table.legend.length > 0 && (
              <Alert type="info" showIcon style={{ marginBottom: 8 }} message={<span style={{ fontSize: 12 }}>Legend found in the sheet: {table.legend.join(", ")}{table.labels && table.labels.length ? ` - contractor labels ${table.labels.join(", ")}` : ""}</span>} />
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
              <Input.Search allowClear size="small" placeholder="Search rows…" style={{ width: 240 }}
                value={rowSearch} onChange={(e) => setRowSearch(e.target.value)} />
              {rowQuery && (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {matchingRows.length} of {table.rows.length} rows match · {shownSelected} of them selected
                </Typography.Text>
              )}
            </div>
            <Table
              size="small" rowKey="i" columns={previewCols} dataSource={previewRows} pagination={false}
              scroll={{ x: Math.max(700, 60 + previewCols.length * 180), y: 360 }}
              rowClassName={(r) => (selected.has(Number(r.i)) ? "" : "import-row-off")}
              locale={{ emptyText: rowQuery ? `No rows match "${rowSearch.trim()}"` : "No rows" }}
            />
            {!rowQuery && table.rows.length > 25 && (
              <a style={{ fontSize: 12 }} onClick={() => setShowAll((v) => !v)}>{showAll ? "Show first 25 rows" : `Show all ${table.rows.length} rows`}</a>
            )}
          </div>
        ),
      }]}
    />
  );
}
